import { z } from 'zod';

/**
 * Single source of truth for client-side validation (Zod).
 *
 * Password policy (enforced wherever a user sets/changes a password):
 *   - min 12 characters
 *   - >= 1 uppercase, >= 1 lowercase, >= 1 number, >= 1 special character
 * The same single message is surfaced for every rule so the UI shows one
 * clear requirement string rather than a moving target as the user types.
 */
export const PASSWORD_REQUIREMENTS_MESSAGE =
  'Password must be at least 12 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.';

export const passwordSchema = z
  .string()
  .min(12, PASSWORD_REQUIREMENTS_MESSAGE)
  .regex(/[A-Z]/, PASSWORD_REQUIREMENTS_MESSAGE)
  .regex(/[a-z]/, PASSWORD_REQUIREMENTS_MESSAGE)
  .regex(/[0-9]/, PASSWORD_REQUIREMENTS_MESSAGE)
  // Any non-alphanumeric character counts as "special" (covers !@#$%^&* etc.)
  .regex(/[^A-Za-z0-9]/, PASSWORD_REQUIREMENTS_MESSAGE);

/**
 * Validate a single password value against the policy.
 * Returns the first failing message, or null when valid.
 */
export function validatePasswordValue(value: string): string | null {
  const result = passwordSchema.safeParse(value);
  return result.success ? null : result.error.issues[0]?.message ?? PASSWORD_REQUIREMENTS_MESSAGE;
}

/**
 * Schema for "set a new password" forms (setup / reset / change).
 * Enforces the policy on `password` and that `confirmPassword` matches.
 */
export const newPasswordFormSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * Account Settings → User Information.
 * All fields are mandatory. (There is no bio/Info field in this form today;
 * if one is added later it should be `.optional()` per the spec.)
 */
export const accountProfileSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  displayName: z.string().trim().min(1, 'Display name is required'),
  timezone: z.string().trim().min(1, 'Timezone is required'),
  locale: z.string().trim().min(1, 'Language is required'),
});

export type AccountProfileInput = z.infer<typeof accountProfileSchema>;

/**
 * Edit Customer modal (Customer Profile page).
 * Name/Email/Phone are mandatory; phone must be exactly 10 digits.
 * Timezone is optional to stay consistent with Create (POST /api/customers
 * allows no timezone, so most customers have none) — requiring it here would
 * block editing Name/Email/Phone for the common timezone-less customer.
 */
export const customerEditSchema = z.object({
  name: z.string().trim().min(1, 'This field is required'),
  email: z
    .string()
    .trim()
    .min(1, 'This field is required')
    .email('Enter a valid email address'),
  phone: z
    .string()
    .trim()
    .min(1, 'This field is required')
    .regex(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
  timezone: z.string().trim().optional(),
});

export type CustomerEditInput = z.infer<typeof customerEditSchema>;

/**
 * Flatten a ZodError into a `{ fieldName: firstMessage }` map for rendering
 * field-level errors. Nested paths are joined with '.'.
 */
export function toFieldErrors(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join('.') || '_';
    if (!errors[key]) errors[key] = issue.message;
  }
  return errors;
}
